// AiCandidateRow — single candidate row.
//
// Renders the predicate / value, the source-vs-AI distinction (icon +
// label, never colour alone), the confidence chip in mono with two-decimal
// precision, the review-status chip with non-color cue, the provenance
// footer, and the tonal Approve / Reject actions.
//
// Per WP-9 design rule the row never claims trust without provenance:
// when `vm.missingTrust` is true the parent (`AiCandidateList`) renders a
// `<Banner>` warning instead of this row.

import { Chip, Icon, Stack, tokens, type ChipVariant } from "@/design";
import type { CandidateFact } from "@/app/scenePackage";
import type { AiCandidateRowVM } from "./aiCandidateAdapter";
import { AiCandidateActions } from "./AiCandidateActions";
import { AiProvenanceFooter } from "./AiProvenanceFooter";

export interface AiCandidateRowProps {
  readonly vm: AiCandidateRowVM;
  /** Override the review status (used when an in-flight action has not
   *  yet round-tripped to the platform). */
  readonly overrideStatus?: CandidateFact["reviewStatus"];
  readonly disabled?: boolean;
  readonly busy?: boolean;
  readonly onApprove: (candidateId: string) => void;
  readonly onReject: (candidateId: string) => void;
}

const REVIEW_STATUS_VARIANT: Record<
  CandidateFact["reviewStatus"],
  ChipVariant
> = {
  pending: "warning",
  approved: "success",
  rejected: "error",
  superseded: "neutral",
};

const REVIEW_STATUS_ICON: Record<
  CandidateFact["reviewStatus"],
  "pending" | "verified" | "block" | "history"
> = {
  pending: "pending",
  approved: "verified",
  rejected: "block",
  superseded: "history",
};

const REVIEW_STATUS_LABEL: Record<CandidateFact["reviewStatus"], string> = {
  pending: "Pending review",
  approved: "Approved",
  rejected: "Rejected",
  superseded: "Superseded",
};

const SOURCE_BADGE_VARIANT: Record<
  AiCandidateRowVM["sourceIcon"],
  ChipVariant
> = {
  fact_check: "success",
  auto_awesome: "tertiary",
};

export function AiCandidateRow({
  vm,
  overrideStatus,
  disabled,
  busy,
  onApprove,
  onReject,
}: AiCandidateRowProps) {
  const status = overrideStatus ?? vm.reviewStatus;
  const isApproved = status === "approved";
  const isRejected = status === "rejected";

  return (
    <li
      data-testid={`ai-candidate-row-${vm.id}`}
      data-source={vm.source}
      data-status={status}
      style={{
        listStyle: "none",
        padding: tokens.space[3],
        marginBottom: tokens.space[2],
        background: tokens.color.surface[2],
        border: tokens.border.thin,
        borderRadius: tokens.radius.md,
      }}
    >
      <Stack
        direction="row"
        align="flex-start"
        justify="space-between"
        gap={2}
        style={{ flexWrap: "wrap" }}
      >
        <div style={{ minWidth: 0, flex: "1 1 auto" }}>
          <Stack direction="row" align="center" gap={2} style={{ flexWrap: "wrap" }}>
            <Chip
              icon={vm.sourceIcon}
              label={vm.sourceLabel}
              variant={SOURCE_BADGE_VARIANT[vm.sourceIcon]}
              data-testid="ai-source-chip"
            />
            <span
              style={{
                fontFamily: tokens.font.family.mono,
                fontSize: tokens.font.role.engMono.size,
                color: tokens.color.ink.primary,
              }}
              aria-label={`Confidence ${vm.confidenceLabel || "missing"}`}
              data-testid="ai-confidence-mono"
            >
              {vm.confidenceLabel || "—.—"}
            </span>
            <Chip
              icon={REVIEW_STATUS_ICON[status]}
              label={REVIEW_STATUS_LABEL[status]}
              variant={REVIEW_STATUS_VARIANT[status]}
              data-testid="ai-status-chip"
            />
          </Stack>
          <div style={{ marginTop: tokens.space[2] }}>
            <strong
              style={{
                display: "block",
                color: tokens.color.ink.primary,
                fontSize: tokens.font.role.body.size,
                lineHeight: tokens.font.role.body.lineHeight,
              }}
            >
              {vm.predicate}
            </strong>
            <span
              style={{
                color: tokens.color.ink.secondary,
                fontSize: tokens.font.role.engMeta.size,
              }}
            >
              {vm.displayValue || "(no literal value)"}
            </span>
          </div>
          <Stack
            direction="row"
            align="center"
            gap={2}
            style={{ marginTop: tokens.space[1], flexWrap: "wrap" }}
          >
            <Icon name="account_tree" size={16} decorative />
            <span
              style={{
                fontFamily: tokens.font.family.mono,
                fontSize: tokens.font.role.engMono.size,
                color: tokens.color.ink.muted,
              }}
            >
              {vm.subjectNodeId}
            </span>
            <span
              style={{
                color: tokens.color.ink.muted,
                fontSize: tokens.font.role.engMeta.size,
              }}
            >
              {vm.factType}
            </span>
          </Stack>
        </div>
      </Stack>

      <AiProvenanceFooter
        modelLabel={vm.modelVersion ?? vm.ruleVersion}
        timestamp={vm.timestamp}
        datasetHashShort={vm.datasetHashShort}
      />

      <AiCandidateActions
        approved={isApproved}
        rejected={isRejected}
        disabled={disabled}
        busy={busy}
        onApprove={() => onApprove(vm.id)}
        onReject={() => onReject(vm.id)}
        approveAriaLabel={`Approve candidate ${vm.predicate}`}
        rejectAriaLabel={`Reject candidate ${vm.predicate}`}
      />
    </li>
  );
}
